import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Brain, Send, X } from 'lucide-react';
import './AIChatWidget.css';

// =============================================================================
// PAGE CONTEXT — the AI adapts its greeting + suggestions per page
// =============================================================================

const PAGE_CONTEXTS = {
  '/datasets/congress': {
    title: 'Congress AI Analyst',
    greeting: "I'm your Congressional trading analyst. Ask me about politician trades, STOCK Act disclosures, party trends, or any specific member's activity.",
    suggestions: [
      "Who's buying the most right now?",
      "Any unusual trades this week?",
      "Compare Dem vs Rep trading",
      "Top performing politicians",
    ],
    placeholder: "Ask about congressional trades...",
  },
  '/datasets/insider': {
    title: 'Insider Trading AI',
    greeting: "I'm your Insider Trading analyst. Ask me about Form 4 filings, cluster buy signals, insider sentiment, or any stock's insider activity.",
    suggestions: [
      "Which insiders are buying most?",
      "Show me cluster buy signals",
      "Any red flags in recent sells?",
      "Top sectors for insider buys",
    ],
    placeholder: "Ask about insider trading...",
  },
  '/datasets/institutional': {
    title: 'Institutional AI Analyst',
    greeting: "I'm your Institutional Holdings analyst. Ask me about 13F filings, hedge fund moves, whale positions, or sector flows.",
    suggestions: [
      "What are hedge funds buying?",
      "Biggest position changes",
      "Which funds are most active?",
      "Sector rotation signals",
    ],
    placeholder: "Ask about institutional holdings...",
  },
  '/politician/': {
    title: 'Politician AI Analyst',
    greeting: "I can analyze this politician's trading patterns, sector exposure, timing, and compare their performance to benchmarks.",
    suggestions: [
      "Analyze their recent trades",
      "What sectors do they favor?",
      "How do they compare to S&P?",
      "Any suspicious timing?",
    ],
    placeholder: "Ask about this politician...",
  },
  '/insider/': {
    title: 'Insider Profile AI',
    greeting: "I can analyze this insider's trading patterns, conviction signals, and how their transactions correlate with stock performance.",
    suggestions: [
      "Is this insider a conviction buyer?",
      "How do their trades perform?",
      "Scheduled vs discretionary sales?",
      "Compare to other insiders",
    ],
    placeholder: "Ask about this insider...",
  },
  '/stock/': {
    title: 'Stock AI Analyst',
    greeting: "I can analyze this stock's insider activity, institutional ownership changes, congressional interest, and smart money signals.",
    suggestions: [
      "Any insider buying recently?",
      "Institutional ownership changes?",
      "Congress members trading this?",
      "Overall smart money signal?",
    ],
    placeholder: "Ask about this stock...",
  },
};

// Fallback for any datasets page not explicitly listed
const DEFAULT_CONTEXT = {
  title: 'ELEMENTAL AI',
  greeting: "I'm your ELEMENTAL AI analyst. Ask me about insider trades, congressional activity, institutional holdings, or any stock.",
  suggestions: [
    "What's the smart money doing?",
    "Any notable insider activity?",
    "Top congressional trades",
    "Market sentiment analysis",
  ],
  placeholder: "Ask ELEMENTAL AI...",
};

const getPageContext = (pathname) => {
  // Exact match first
  if (PAGE_CONTEXTS[pathname]) return PAGE_CONTEXTS[pathname];

  // Prefix match for dynamic routes
  for (const [prefix, ctx] of Object.entries(PAGE_CONTEXTS)) {
    if (prefix.endsWith('/') && pathname.startsWith(prefix)) return ctx;
  }

  // Any other /datasets/ page gets the default
  if (pathname.startsWith('/datasets/')) return DEFAULT_CONTEXT;

  return DEFAULT_CONTEXT;
};

// =============================================================================
// MOCK AI RESPONSES — replaced with Claude API in Week 5-6
// =============================================================================

const getMockResponse = (query, pathname) => {
  const q = query.toLowerCase();

  // Insider-specific
  if (q.includes('cluster') || q.includes('signal')) {
    return "There are 4 active cluster buy signals right now. The strongest is LUMN — 4 executives purchased $2.1M in shares over 10 days. OXY is next with 3 insiders buying $4.2M. Historically, cluster buys of 3+ insiders within 14 days have a 72% hit rate for positive 30-day returns.";
  }
  if (q.includes('insider') && (q.includes('buy') || q.includes('buyer'))) {
    return "Top insider buyers this month: Kate Johnson (CEO, LUMN) leads with $615K in purchases — stock up 8.7% since. Vicki Hollub (CEO, OXY) bought $1.37M, now up 7.2%. CEO-level purchases have outperformed director-level buys by 3.2% on average over the past 90 days.";
  }

  // Congress-specific
  if (q.includes('congress') || q.includes('politician') || q.includes('pelosi')) {
    return "Congressional trading surged 34% this week. Nancy Pelosi filed $3M in NVDA call options ahead of the AI infrastructure bill vote. Republican members are increasing Defense sector allocation (+18% MoM) while Democrats concentrate on Tech (+12% MoM). 7 convergence signals detected where congressional and insider trades align.";
  }
  if (q.includes('dem') || q.includes('rep') || q.includes('party')) {
    return "Democrats are net buyers in Technology ($847M) and Healthcare ($312M). Republicans favor Defense ($523M) and Energy ($445M). Both parties show heavy buying in semiconductors — likely tied to upcoming CHIPS Act funding announcements. The bipartisan overlap on NVDA is notable.";
  }

  // Stock-specific
  if (q.includes('institutional') || q.includes('hedge fund') || q.includes('whale')) {
    return "Net institutional flow is +$12.4B this month. Tech leads at +$2.8B, followed by Financials at +$1.2B. Notable: Citadel opened a new $456M NVDA position, Berkshire added 8.4M OXY shares, and Renaissance Technologies fully exited TSLA. ARK continues accumulating PLTR despite price weakness.";
  }

  // Red flags
  if (q.includes('red flag') || q.includes('sell') || q.includes('concern') || q.includes('suspicious')) {
    return "Notable sells: Alexander Karp (PLTR CEO) sold $28.9M — largest single disposal this year, stock down 6.2%. UBER's 10% owner sold $39.2M. However, most tech CEO sells (Cook, Huang, Jassy) appear to be scheduled 10b5-1 plans rather than discretionary. I'd focus on unscheduled sales for true conviction signals.";
  }

  // Sector
  if (q.includes('sector')) {
    return "Smart money by sector: Technology (+$2.8B institutional, but insider selling heavy — mostly scheduled). Energy shows strongest insider conviction (OXY cluster buy). Healthcare has notable CEO buying at LLY and MNRO. Financials seeing C-suite accumulation at JPM. Real Estate and Utilities seeing outflows.";
  }

  // General / catch-all
  if (pathname.startsWith('/stock/')) {
    return "Based on available data, this stock shows mixed signals. Check the insider trading tab for recent Form 4 filings, and institutional ownership for 13F changes. I can provide deeper analysis on specific aspects — just ask about insider activity, institutional flows, or technical patterns.";
  }

  return "The overall smart money picture shows a cautiously bullish stance. Insider buy/sell ratio is neutral at 1.0:1, but conviction on buys is higher (avg buy $890K). Strongest signals: LUMN, OXY, and JPM with multi-insider cluster buys. Institutional flows favor Tech and Financials. Want me to drill deeper into any specific area?";
};

// =============================================================================
// ROUTES WHERE THE FAB SHOULD APPEAR
// =============================================================================

const SHOW_ON_ROUTES = [
  '/datasets/',    // All dataset pages
  '/politician/',  // Politician detail pages
  '/insider/',     // Insider profile pages
  '/stock/',       // Stock overview pages
];

const shouldShowFAB = (pathname) => {
  return SHOW_ON_ROUTES.some(route => pathname.startsWith(route));
};

// =============================================================================
// COMPONENT
// =============================================================================

const AIChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const prevPathRef = useRef(location.pathname);

  const context = getPageContext(location.pathname);

  // Listen for external open requests (e.g., from "Ask More" buttons)
  useEffect(() => {
    const handleOpenChat = (e) => {
      setIsOpen(true);
      if (e.detail?.prompt) {
        // Pre-fill input with the prompt
        setInput(e.detail.prompt);
      }
    };
    window.addEventListener('open-ai-chat', handleOpenChat);
    return () => window.removeEventListener('open-ai-chat', handleOpenChat);
  }, []);

  // Reset chat when navigating to a different page
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setMessages([]);
      setIsTyping(false);
      setInput('');
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Initialize greeting when messages are empty and chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'ai', text: context.greeting }]);
    }
  }, [isOpen, messages.length, context.greeting]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Don't render on excluded pages
  if (!shouldShowFAB(location.pathname)) return null;

  const handleSend = (text = input) => {
    if (!text.trim() || isTyping) return;

    setMessages(prev => [...prev, { role: 'user', text: text.trim() }]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        role: 'ai',
        text: getMockResponse(text, location.pathname)
      }]);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* FAB Button */}
      <button
        className={`ai-fab ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Ask ELEMENTAL AI"
      >
        {isOpen ? <X size={22} /> : <Brain size={22} />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <>
          <div className="ai-chat-overlay" onClick={() => setIsOpen(false)} />
          <div className="ai-chat-window">
            <div className="ai-chat-header">
              <div className="ai-chat-header-left">
                <div className="ai-chat-header-icon">
                  <Brain size={16} color="#fff" />
                </div>
                <div>
                  <h3>{context.title}</h3>
                  <p>Powered by ELEMENTAL</p>
                </div>
              </div>
              <button className="ai-chat-close" onClick={() => setIsOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="ai-chat-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`ai-chat-msg ${msg.role}`}>
                  <span className="msg-label">{msg.role === 'ai' ? 'AI Analyst' : 'You'}</span>
                  {msg.text}
                </div>
              ))}
              {isTyping && (
                <div className="ai-chat-typing">
                  <span /><span /><span />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 1 && (
              <div className="ai-chat-suggestions">
                {context.suggestions.map((s, i) => (
                  <button key={i} className="ai-chat-suggestion" onClick={() => handleSend(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="ai-chat-input-area">
              <input
                type="text"
                placeholder={context.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button
                className="ai-chat-send"
                onClick={() => handleSend()}
                disabled={!input.trim() || isTyping}
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AIChatWidget;